import { useEffect, useState } from "react";
import { IndexTable, LegacyCard, Tabs, Text, useIndexResourceState } from "@shopify/polaris";
import { HomeTableAccess } from "./libs/store";
import { ParsedTableDataContext } from "./libs/context/parsedDataContext";
import { useBulkActions } from "./libs/hooks/useBulkActions";
import { usePromotedBulkActions } from "./libs/hooks/usePromotedBulkActions";
import { useIndexTableHeadingSortInfo } from "./libs/hooks/useIndexTableHeadingSortInfo";
import { BulkActionCompletedContext } from "./libs/context/bulkActionCompletedContext";
import { NoUpcomingWrapper } from "@vendor-app/app/_sharedComponents/TypedTable/usePolarisTypedTable";
import { HomeTableModals } from "./Modals";
import { SelectionType } from "@vendor-app/app/AdminRoute/AdminSubscriptionDetailsRoute/libs";
import { useParsedTableData } from "./libs/hooks/useParseTableData";
import { homeTableTabs } from "./libs/constants";
import { HomePageTableTab } from "../../utils";
import { HomeTablePagination } from "./Pagination";
import { useTabManager } from "./libs/hooks/useTabManager";
import { TableFilteringRange, TableRows } from "./libs/components";
import { useHeadings } from "./libs/hooks/useHeadings";

export const HomeTable = () => {
  const [selectedTab, setSelectedTab] = useState(HomePageTableTab.ACTIVE);
  const { getTabConfiguration } = useTabManager();
  const tabConfig = getTabConfiguration(selectedTab);

  const data = useParsedTableData(selectedTab);
  const headings = useHeadings(tabConfig);

  const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(data);
  const promotedBulkActions = usePromotedBulkActions();
  const bulkActions = useBulkActions();
  const sortInfo = useIndexTableHeadingSortInfo();

  const actions = HomeTableAccess.useActions();
  const isTableLoading = HomeTableAccess.useIsLoading();

  const handleTabChange = (tab: number) => {
    const newTab = tab as HomePageTableTab;
    const newTabConfig = getTabConfiguration(newTab);

    actions.filter.reset();
    actions.filter.update(newTabConfig.filters);

    actions.column.update(newTabConfig.defaultColumns);

    setSelectedTab(newTab);
  };

  useEffect(() => {
    actions.data.updateSelectionById(selectedResources);
  }, [selectedResources]);

  useEffect(() => {
    actions.pagination.firstPage();
    actions.filter.update(tabConfig.filters);
  }, []);

  return (
    <BulkActionCompletedContext.Provider
      value={{
        onCompleted: () => handleSelectionChange(SelectionType.All, false),
      }}
    >
      <LegacyCard>
        <div style={{ position: "relative" }}>
          <LegacyCard.Section flush>
            <Tabs tabs={homeTableTabs} selected={selectedTab} onSelect={handleTabChange}>
              <TableFilteringRange selectedTab={selectedTab} />
              <IndexTable
                loading={isTableLoading}
                selectable
                headings={headings}
                itemCount={data.length}
                selectedItemsCount={allResourcesSelected ? "All" : selectedResources.length}
                onSelectionChange={handleSelectionChange}
                promotedBulkActions={promotedBulkActions}
                bulkActions={bulkActions}
                emptyState={
                  <NoUpcomingWrapper>
                    <Text variant="bodyLg" as="span" color="subdued">
                      {selectedTab === HomePageTableTab.ACTIVE
                        ? "No upcoming orders found"
                        : "No recent cancellations found"}
                    </Text>
                  </NoUpcomingWrapper>
                }
                resourceName={{
                  singular: "subscription",
                  plural: "subscriptions",
                }}
                sortColumnIndex={sortInfo.currentSortColumn}
                sortDirection={sortInfo.direction}
                onSort={sortInfo.onSort}
                sortable={sortInfo.sortableColumns}
              >
                <ParsedTableDataContext.Provider value={data}>
                  <TableRows />
                </ParsedTableDataContext.Provider>
              </IndexTable>
            </Tabs>
          </LegacyCard.Section>
          <LegacyCard.Section subdued>
            <HomeTablePagination />
          </LegacyCard.Section>
        </div>
      </LegacyCard>
      <HomeTableModals />
    </BulkActionCompletedContext.Provider>
  );
};
